// components/ui/Card.tsx
import { HTMLAttributes, forwardRef } from 'react';
import { clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';
import { motion } from 'framer-motion';

interface CardProps extends HTMLAttributes<HTMLDivElement> {
    hover?: boolean;
    glass?: boolean;
    animate?: boolean;
    delay?: number;
    highlight?: boolean;
    className?: string;
    children?: React.ReactNode;
}

const Card = forwardRef<HTMLDivElement, CardProps>(
    ({ className, hover, glass, animate = true, delay = 0, highlight, children, ...props }, ref) => {
        const cardClasses = twMerge(clsx(
            'bg-white dark:bg-slate-900 text-slate-900 dark:text-slate-100 rounded-2xl border border-slate-200 dark:border-slate-800 shadow-sm transition-all duration-300',
            hover && 'hover:shadow-lg hover:border-emerald-300 dark:hover:border-teal-500/50 hover:-translate-y-1 cursor-pointer',
            glass && 'backdrop-blur-xl bg-white/90 dark:bg-slate-900/90 border-white/50 dark:border-slate-800/80',
            highlight && 'ring-2 ring-emerald-500/20 shadow-emerald-500/5',
            className
        ));

        const content = (
            <div ref={ref} className={cardClasses} {...props}>
                {children}
            </div>
        );

        if (animate) {
            return (
                <motion.div
                    initial={{ opacity: 0, y: 20, scale: 0.98 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{
                        duration: 0.5,
                        delay,
                        ease: [0.16, 1, 0.3, 1],
                    }}
                >
                    {content}
                </motion.div>
            );
        }

        return content;
    }
);
Card.displayName = 'Card';

// ============================================================
// CARD HEADER
// ============================================================
interface CardHeaderProps extends HTMLAttributes<HTMLDivElement> {
    gradient?: boolean;
}

const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
    ({ className, gradient, children, ...props }, ref) => (
        <div
            ref={ref}
            className={twMerge(clsx(
                'px-6 py-4 border-b border-slate-100 dark:border-slate-800',
                gradient && 'bg-gradient-to-r from-emerald-50/50 via-white to-white dark:from-slate-900 dark:via-slate-900 dark:to-slate-900',
                className
            ))}
            {...props}
        >
            {children}
        </div>
    )
);
CardHeader.displayName = 'CardHeader';

// ============================================================
// CARD CONTENT
// ============================================================
const CardContent = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
    ({ className, ...props }, ref) => (
        <div
            ref={ref}
            className={twMerge(clsx('p-6 text-slate-800 dark:text-slate-200', className))}
            {...props}
        />
    )
);
CardContent.displayName = 'CardContent';

// ============================================================
// CARD FOOTER
// ============================================================
const CardFooter = forwardRef<HTMLDivElement, HTMLAttributes<HTMLDivElement>>(
    ({ className, ...props }, ref) => (
        <div
            ref={ref}
            className={twMerge(clsx(
                'px-6 py-4 border-t border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-slate-950/50 rounded-b-2xl',
                className
            ))}
            {...props}
        />
    )
);
CardFooter.displayName = 'CardFooter';

export { Card, CardHeader, CardContent, CardFooter };
export type { CardProps, CardHeaderProps };